/**
 * JunctionGrantRow: one per-client grant inside a connected-client card of
 * `SharedLocationsCard` (label and help on the left, the control on the right).
 *
 * A pure move of the row markup the card already used for "Let this client
 * adjust their own stock here" and "Stocktake review": the classes are
 * verbatim, so those rows render byte-identical DOM after adopting it (the
 * 12px `mt-3` / `p-3` lives here and nowhere in new layout code). Callers pass
 * content, never spacing.
 *
 * `id` lands on the control (the label's `htmlFor`), `helpId` on the help
 * paragraph (the control's `aria-describedby`). `children` render under the
 * help, inside the same column, for a one-line note that belongs to this grant.
 */
import type { ReactNode } from 'react';

export interface JunctionGrantRowProps {
  /** The control's id; the label points at it. */
  id: string;
  /** The help paragraph's id; pass it to the control's `aria-describedby`. */
  helpId: string;
  label: ReactNode;
  help: ReactNode;
  /** The control on the right (a Switch or a Select). */
  control: ReactNode;
  /** Optional lines under the help (a note about this grant). */
  children?: ReactNode;
}

export function JunctionGrantRow({ id, helpId, label, help, control, children }: JunctionGrantRowProps) {
  return (
    <div className="mt-3 flex items-start justify-between gap-4 rounded-md border bg-muted/30 p-3">
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="text-sm font-medium leading-snug">
          {label}
        </label>
        <p id={helpId} className="text-xs text-muted-foreground mt-1.5">
          {help}
        </p>
        {children}
      </div>
      {control}
    </div>
  );
}
